import React from "react";
import { motion } from "framer-motion";
import { Handshake, Award, Users, MessageSquare, DollarSign } from "lucide-react";

const FloatingParticle = ({ delay, x, y, size = 4 }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      left: `${x}%`,
      top: `${y}%`,
      background: "linear-gradient(135deg, #60a5fa, #818cf8)",
    }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{
      opacity: [0, 0.6, 0],
      scale: [0, 1, 0],
      y: [0, -30, -60],
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      ease: "easeOut",
    }}
  />
);

const FellowsDentistPartnerSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div
        className="absolute top-[-200px] left-[-100px] w-[700px] h-[700px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <FloatingParticle delay={0} x={20} y={30} size={5} />
      <FloatingParticle delay={0.8} x={75} y={60} size={4} />
      <FloatingParticle delay={1.6} x={40} y={70} size={6} />
      <FloatingParticle delay={2.2} x={85} y={25} size={3} />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1050px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl lg:text-[46px] font-extrabold text-slate-900 tracking-tight leading-tight">
            Become a Certified{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Asha Dentist Partner.
            </span>
          </h2>
          <p className="text-[16px] text-slate-500 font-medium mt-4 max-w-2xl mx-auto">
            Once you understand enough about the Asha platform, we'll approve you to share how Asha works with colleagues and other offices. If they sign up, you earn <span className="font-bold text-slate-700">12% of net revenue for their first year</span>.
          </p>
        </motion.div>

        {/* Content card */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="w-full rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative"
        >
          <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #0ea5e9 100%)" }} />

          <div className="p-10">
            <div className="grid grid-cols-2 gap-8">
              {/* How it works */}
              <div>
                <h3 className="text-[20px] font-bold text-slate-900 mb-4">How It Works</h3>
                <div className="space-y-5">
                  {[
                    {
                      icon: Award,
                      title: "Get Certified",
                      desc: "As you learn the platform through your own practice, you'll naturally build the knowledge needed. Once you're ready, we approve you as a Certified Dentist Partner.",
                      iconBg: "bg-blue-100",
                      iconBorder: "border-blue-200",
                      iconColor: "text-blue-600",
                    },
                    {
                      icon: MessageSquare,
                      title: "Share With Colleagues",
                      desc: "Talk to other offices about how Asha has worked for your practice. Share your referral link, make an intro — whatever feels natural.",
                      iconBg: "bg-indigo-100",
                      iconBorder: "border-indigo-200",
                      iconColor: "text-indigo-600",
                    },
                    {
                      icon: DollarSign,
                      title: "Earn 12% Revenue Share",
                      desc: "For every office that signs up through you, you receive 12% of their net revenue for the entire first year. No caps, no limits on referrals.",
                      iconBg: "bg-sky-100",
                      iconBorder: "border-sky-200",
                      iconColor: "text-sky-600",
                    },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.35 + i * 0.1, duration: 0.4 }}
                      className="flex gap-4 items-start"
                    >
                      <div className={`w-10 h-10 rounded-xl ${item.iconBg} flex items-center justify-center shrink-0 mt-0.5 shadow-sm border ${item.iconBorder}`}>
                        <item.icon className={`w-5 h-5 ${item.iconColor}`} />
                      </div>
                      <div>
                        <h4 className="text-[15px] font-bold text-slate-900 mb-1">{item.title}</h4>
                        <p className="text-[13px] text-slate-500 leading-relaxed font-medium">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Right: key details */}
              <div>
                <h3 className="text-[20px] font-bold text-slate-900 mb-4">Program Details</h3>
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="rounded-[20px] bg-blue-50/60 border border-blue-100/60 p-6 space-y-4"
                >
                  {[
                    { label: "Revenue Share", value: "12% of net revenue" },
                    { label: "Duration", value: "Full first year per referral" },
                    { label: "Cap", value: "$100k per referred entity" },
                    { label: "How to Qualify", value: "Approved after platform proficiency" },
                    { label: "Tracking", value: "Dedicated partner dashboard" },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.5 + i * 0.08 }}
                      className="flex items-center justify-between py-2 border-b border-blue-100/50 last:border-0 last:pb-0"
                    >
                      <span className="text-[13px] font-semibold text-slate-500">{item.label}</span>
                      <span className="text-[13px] font-bold text-slate-900">{item.value}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FellowsDentistPartnerSlide;
